import { useFormContext } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { CustomFieldDefinition } from '@/types';
import { CustomFieldTypes } from '@/types/enums';
import { FormSelect } from '@/components/shared/FormSelect';
import { AlertCircle } from 'lucide-react';

interface DynamicFormProps {
  field: CustomFieldDefinition;
  name?: string;
  displayValue?: any;
  isEditMode?: boolean;
  isCreateMode?: boolean;
}

export const CustomFieldDisplay = ({
  field,
  name,
  displayValue,
  isEditMode = false,
  isCreateMode = false,
}: DynamicFormProps) => {
  displayValue = displayValue?.[field.fieldName];

  const {
    register,
    formState: { errors },
  } = useFormContext();

  let fieldName = field.fieldName;
  //@ts-ignore
  let customFieldError = errors[field.fieldName];

  if (name) {
    fieldName = `${name}.${field.fieldName}`;
    //@ts-ignore
    customFieldError = errors[name]?.[field.fieldName];
  }

  const validationRules = {
    required: isEditMode ? false : field.isRequired,
  };

  // Read-only display logic when editing form
  // If creating ticket we want it to act as an input field
  if ((!field.isEditable || !isEditMode) && !isCreateMode) {
    const formatDisplayValue = () => {
      if (
        displayValue === null ||
        displayValue === undefined ||
        displayValue === ''
      ) {
        return <span className="text-muted-foreground">Not provided</span>;
      }

      switch (field.fieldType) {
        case CustomFieldTypes.BOOLEAN:
          return displayValue ? 'Yes' : 'No';
        case CustomFieldTypes.DATE:
          return new Date(displayValue).toLocaleDateString();
        case CustomFieldTypes.SELECT:
          if (Array.isArray(displayValue)) {
            return (
              <FormSelect
                name={fieldName}
                disabled={true}
                // label={field.fieldLabel}
                multiple={true}
                options={
                  displayValue?.map((option) => ({
                    value: option || '',
                    label: option || '',
                  })) || []
                }
              />
            );
          }
          return String(displayValue);
        default:
          return String(displayValue);
      }
    };

    return (
      <div className="space-y-2 pt-2">
        <Label className="text-sm font-medium text-muted-foreground">
          {field.fieldLabel}
        </Label>
        <p className="text-sm text-foreground">{formatDisplayValue()}</p>
      </div>
    );
  }

  // Editable field rendering logic
  const renderEditableField = () => {
    switch (field.fieldType) {
      case CustomFieldTypes.TEXT:
        return (
          <Input
            id={fieldName}
            placeholder={field.description || ''}
            // className="min-h-[100px]"
            {...register(fieldName, validationRules)}
          />
        );

      case CustomFieldTypes.NUMBER:
        return (
          <Input
            id={fieldName}
            type="number"
            placeholder={field.description || 'Enter a number'}
            {...register(fieldName, {
              ...validationRules,
              valueAsNumber: true,
            })}
          />
        );

      case CustomFieldTypes.BOOLEAN:
        return (
          <div className="flex items-center space-x-2 pt-2">
            <Checkbox
              id={fieldName}
              {...register(fieldName, validationRules)}
            />
            <Label
              htmlFor={fieldName}
              className="text-sm font-medium text-muted-foreground cursor-pointer"
            >
              {field.fieldLabel}
              {field.isRequired && !isEditMode && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </Label>
          </div>
        );

      case CustomFieldTypes.DATE:
        return (
          <Input
            id={fieldName}
            type="date"
            placeholder={field.description || 'Select a date'}
            {...register(fieldName, validationRules)}
          />
        );

      case CustomFieldTypes.SELECT:
        return (
          <FormSelect
            name={fieldName}
            multiple={true}
            rules={{ ...validationRules }}
            options={
              field.fieldOptions?.map((option) => ({
                value: option || '',
                label: option || '',
              })) || []
            }
          />
        );

      default:
        return null;
    }
  };

  return (
    <div key={field.id} className="space-y-2 pt-2">
      {field.fieldType !== CustomFieldTypes.BOOLEAN && (
        <Label
          htmlFor={fieldName}
          className="text-sm font-medium text-muted-foreground"
        >
          {field.fieldLabel}
          {field.isRequired && !isEditMode && (
            <span className="text-red-500 ml-1">*</span>
          )}
        </Label>
      )}
      {renderEditableField()}
      {field.fieldType !== CustomFieldTypes.SELECT && customFieldError && (
        <p className="text-xs font-medium text-red-500 flex items-center gap-1.5 px-1">
          <AlertCircle className="w-3.5 h-3.5" />
          {customFieldError.message?.toString() || 'This field is required'}
        </p>
      )}
    </div>
  );
};
